import React, { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Button, Collapse, DatePicker, Flex, notification, Select, Spin, Tag, Tooltip, Typography } from 'antd'
import {
  useGetFxRateHistoriesQuery,
  useUpdateFxRateHistoryMutation,
} from '@/features/portal-configuration/portal-configuration.api.ts'
import { AgGridReact } from 'ag-grid-react'
import { GridOptions, ValueSetterParams } from 'ag-grid-community'
import { FxRateHistoryImportModal } from '@/pages/portal-configuration/components/FxRateHistoryImportModal.tsx'
import { EmptyData, FxRateHistoryType, FxRateHistoryTypeOptions, Month } from '@/common/types/misc.def.ts'
import { FxRateHistory } from '@/features/portal-configuration/portal-configuration.def.ts'
import { v4 } from 'uuid'
import { Decimal } from 'decimal.js'
import { sleep } from '@/utils/misc.ts'
import { handleApiError } from '@/utils/api.tsx'
import { FxCurrency } from '@/common/types/portal-configuration.def.ts'
import { countBy, intersection } from 'lodash-es'
import { EditOutlined } from '@ant-design/icons'
import { QueryErrorWrapper } from '@/common/components/QueryErrorWrapper.tsx'

export const FxRateHistoryConfiguration: React.FC = () => {
  const [dateObj, setDateObj] = useState(dayjs())
  const [fxRateHistoryType, setFxRateHistoryType] = useState(FxRateHistoryType.MonthlyAverage)
  const { data: fxRateHistories, refetch, isFetching, error } = useGetFxRateHistoriesQuery(
    { year: dateObj.year(), type: fxRateHistoryType },
    { skip: !dateObj.year() || !fxRateHistoryType },
  )

  return (
    <Flex vertical gap="middle" className="max-w-[800px]">
      <Spin fullscreen spinning={isFetching} />
      <Flex gap="middle" justify="space-between">
        <Flex vertical gap="small">
          <Flex gap="small" align="center" justify="space-between">
            <Typography.Text>Target Year:</Typography.Text>
            <DatePicker className="w-[250px]" value={dateObj} onChange={date => setDateObj(date)} picker="year" />
          </Flex>
          <Flex gap="small" align="center" justify="space-between">
            <Typography.Text>Type:</Typography.Text>
            <Select
              className="w-[250px]"
              value={fxRateHistoryType}
              onChange={setFxRateHistoryType}
              options={FxRateHistoryTypeOptions}
            />
          </Flex>
        </Flex>
        <Flex gap="small">
          <Button htmlType="button" onClick={refetch}>
            Refresh
          </Button>
          <FxRateHistoryImportModal />
        </Flex>
      </Flex>
      <QueryErrorWrapper error={error}>
        <Collapse
          items={fxRateHistories?.map((x, index) => {
            return {
              key: `${x.year}-${x.month}-${fxRateHistoryType}`,
              label: <FxRateTablePanelLabel index={index} year={dateObj.year()} data={x} />,
              children: <FxRateTable key={`${x.year}-${x.month}-${x.updatedAt}`} data={x} />,
            }
          }) || []}
        />
      </QueryErrorWrapper>
    </Flex>
  )
}

type FxRateTablePanelLabelProps = {
  year: number
  index: number
  data: FxRateHistory
}
const FxRateTablePanelLabel: React.FC<FxRateTablePanelLabelProps> = ({ year, index, data }) => {
  const endOfMonth = dayjs(new Date(year, index, 1)).endOf('month')
  const existing = Object.keys(data.rates)

  const isPassed = dayjs().isAfter(endOfMonth)
  const isMissing = !data || intersection(existing, Object.keys(FxCurrency)).length < Object.keys(FxCurrency).length
  const isEditable = data.type !== FxRateHistoryType.YtdAverageClosing && data.type !== FxRateHistoryType.YtdAverageAverage

  return (
    <Flex justify="space-between">
      <Typography.Text>{Month[index]}</Typography.Text>
      <Flex gap="small" align="flex-end">
        {data.updatedAt && isEditable
          ? <Typography.Text className="text-xs">
            <EditOutlined /> {data.updatedBy}@{dayjs(data.updatedAt).format('YYYY-MM-DD h:mm:ss A')}
          </Typography.Text>
          : null
        }
        {isMissing && isPassed ?
          <Tooltip title="Missing required FX rates"><Tag color="error">Not Completed</Tag></Tooltip> : null}
        {isMissing && !isPassed ?
          <Tooltip title="Not necessary to fill yet"><Tag color="gold">Pending</Tag></Tooltip> : null}
        {!isMissing ?
          <Tooltip title="All required FX rates are filled"><Tag color="green">Completed</Tag></Tooltip> : null}
      </Flex>

    </Flex>
  )
}

type FxRateTableProps = {
  data: FxRateHistory
}
type FxRateItem = {
  guid: string
  currency: string
  usdRate: number
}
const FxRateTable: React.FC<FxRateTableProps> = ({ data }) => {
  const rawItems = data ? Object.keys(data.rates).map(x => ({
    guid: v4(),
    currency: x,
    usdRate: data.rates[x],
  })) as FxRateItem[] : []
  const [rowData, setRowData] = useState(rawItems)
  const [tableTouched, setTableTouched] = useState(false)
  const [updateFxRateHistory, { isLoading }] = useUpdateFxRateHistoryMutation()
  const gridRef = useRef<AgGridReact<FxRateItem>>(null)

  // eslint-disable-next-line
  const updateListItem = useCallback((params: ValueSetterParams<FxRateItem, any>, field: string, newValue?: any) => {
    setRowData(prev => {
      return prev.map(x => {
        if (x.guid == params.data.guid) {
          return {
            ...x,
            [field]: newValue,
          }
        } else {
          return x
        }
      })
    })
    return true
  }, [])

  const gridOptions = useMemo<GridOptions<FxRateItem>>(() => ({
    headerHeight: 30,
    rowHeight: 25,
    singleClickEdit: true,
    columnDefs: [
      {
        colId: 'deleteBtn', headerName: '',
        sortable: false,
        cellRenderer: ({ data }: { data: FxRateItem }) => {
          return (
            <Button
              danger
              htmlType="button"
              size="small"
              className="h-4"
              onClick={() => handleDeleteRow(data.guid)}
            >
              Delete
            </Button>
          )
        },
        maxWidth: 100,
      },
      {
        colId: 'currency', field: 'currency', headerName: 'Currency', flex: 1, editable: true,
        valueGetter: params => params.data?.currency || EmptyData.string,
        valueSetter: params => updateListItem(
          params,
          'currency',
          params.newValue.toUpperCase(),
        ),
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: Object.keys(FxCurrency),
          valueListHeight: 175,
          valueListWidth: 100,
          allowTyping: true,
          cellHeight: 25,
        },
        cellClass: params => {
          if (!params.data?.currency) return ['bg-amber-400']
        },
      },
      {
        colId: 'usdRate',
        field: 'usdRate',
        headerName: 'To USD Rate (5 sig figs)',
        flex: 1,
        editable: true,
        cellEditor: 'agNumberCellEditor',
        valueFormatter: params => params.data?.usdRate?.toString() ?? EmptyData.string,
        valueSetter: params => updateListItem(
          params,
          'usdRate',
          params.newValue != undefined
            ? new Decimal(params.newValue < 1 ? params.newValue.toPrecision(5) : params.newValue.toFixed(5)).toNumber()
            : params.newValue,
        ),
        cellClass: params => {
          if (!params.data?.usdRate) return ['bg-amber-400']
        },
      },
    ],
    suppressContextMenu: true,
  }), [updateListItem])

  const handleAddRow = () => {
    gridRef.current?.api.stopEditing()
    setRowData(prev => [{
      guid: v4(),
      currency: undefined!,
      usdRate: undefined!,
    } as FxRateItem, ...prev])
  }

  const handleAddMissingRow = () => {
    gridRef.current?.api.stopEditing()
    setRowData(prev => {
      const existing = prev.map(x => x.currency)
      const missing = Object.keys(FxCurrency).filter(ccy => !existing.includes(ccy))
      return [...missing.map(x => ({
        guid: v4(),
        currency: x!,
        usdRate: undefined!,
      }) as FxRateItem), ...prev]
    })
  }

  const handleReset = () => {
    if (gridRef.current) {
      gridRef.current.api.stopEditing()
      setRowData(rawItems)
      setTableTouched(false)
      gridRef.current?.api.refreshCells()
      gridRef.current?.api.redrawRows()
    }
  }

  const handleDeleteRow = (guid: string) => {
    setRowData(prev => prev.filter(x => x.guid !== guid))
    setTableTouched(true)
  }

  const handleSave = useCallback(async () => {
    if (gridRef.current) {
      gridRef.current.api.stopEditing()
      await sleep(0) // to allow row data got synced
      const editedRows: FxRateItem[] = []
      gridRef.current.api.forEachNode(rowNode => {
        if (rowNode.data) {
          editedRows.push(rowNode.data)
        }
      })

      const errors: string[] = []
      editedRows.forEach(x => {
        if (errors.length) return
        if (!x.currency) errors.push('Currency cannot be empty')
        if (!x.usdRate) errors.push('To USD Rate cannot be empty')
      })

      const countDict = countBy(editedRows, x => x.currency)
      const duplications = Object.entries(countDict).filter(entry => entry[1] > 1).map(entry => entry[0])
      if (duplications.length) errors.push(`Duplicated currency: ${duplications.join(', ')}`)

      if (errors.length) {
        notification.error({
          message: errors.map((msg, index) => (
            <Typography.Text key={index} className="block">
              {msg}
            </Typography.Text>
          )),
        })
        gridRef.current?.api.redrawRows()
        return
      }

      const newRates: FxRateHistory['rates'] = editedRows.reduce(
        (acc, item) => ({ ...acc, [item.currency]: item.usdRate }),
        {},
      )

      const result = await updateFxRateHistory({
        year: data.year,
        month: data.month,
        type: data.type,
        rates: newRates,
      })
      if ('data' in result) {
        notification.success({ message: 'Update Successfully' })
        setTableTouched(false)
      } else {
        handleApiError(result.error)
      }
    }
  }, [data.month, data.type, data.year, updateFxRateHistory])

  return (
    <Flex vertical gap="small">
      <Flex align="center" justify="space-between">
        <div>
          {tableTouched ? <Typography.Title level={5}>Editing...</Typography.Title> : null}
        </div>
        <Flex gap="small">
          <Button htmlType="button" onClick={handleAddRow}>Add</Button>
          <Button htmlType="button" onClick={handleAddMissingRow}>Add All Missing</Button>
          <Button htmlType="button" danger onClick={handleReset}>Reset</Button>
          <Button htmlType="button" type="primary" disabled={!tableTouched} onClick={handleSave} loading={isLoading}>
            Save
          </Button>
        </Flex>
      </Flex>
      <div className="ag-theme-quartz w-full h-[650px]">
        <AgGridReact
          className="w-full" {...gridOptions}
          ref={gridRef}
          rowData={rowData}
          onCellValueChanged={() => setTableTouched(true)}
        />
      </div>
    </Flex>
  )
}
